import React, { useEffect, useState } from 'react'
import MultiActionBarComponent from '../../../../components/multiActionBar/MultiActionBar.component'
import { IOption } from '../../../../common/common.types'
import BoxComponent from '../../../../components/box/Box.component'
import { useTranslation } from 'react-i18next'
import { getBodyAssessmentSelector } from '../selectors'
import { useDispatch, useSelector } from 'react-redux'
import { BodyAssessmentConstants } from '../store/bodyAssessment.constants'
import { DrainsAndTubesComment } from '../drainsTubes/content/DrainsTubesComment.content'
import { ObstetricsComment } from '../obstetrics/content/ObstetricsComment.content'
import useDeviceMetrics from '../../../../utilities/useDeviceMetrics'
import { IonCol, IonFooter, IonHeader, IonLabel, IonRow, IonToggle, IonToolbar } from '@ionic/react'
import ActionButtons from '../../../../components/actionButtons/ActionButtons.component'
import { ToggleSegmentButtons } from '../../../../components/segmentButtons/toggleSegmentButtons/ToggleSegmentButtons.component'
import { IBurnsState } from '../burns/store/burns.types'
import { getBurnsSelector } from '../burns/selectors'
import { ISegmentButton } from '../../../../components/segmentButtons/SegmentButtons.component'
import { saveBurns, setBodyAreaCalculations, setBurnColors, setBurnExams } from '../burns/store/burns.actions'
import { isTablet } from '../../../../utilities'
import { setBodySelectionMode } from '../store/bodyAssessment.actions'
import { getBodySurveySelector } from '../bodySurvey/selectors'
import { IDataPointsConfig } from '../../../configurations/store/configurations.types'
import { getDatapointsConfigSelector } from '../../../configurations/selectors'
import {
  setDocumentFindings,
  setExaminationInProgress,
  setZoomVisible,
  setSelectedBodyPart as selectedBodyPartAction,
} from '../bodySurvey/store/bodySurvey.actions'
import './ExamBar.component.css'
import { getDocumentedLocations } from '../../../../utilities/bodySurvey.util'

const ExamBarcomponent: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const { isTabletInLandscape } = useDeviceMetrics()
  const { selectedExam, bodySelectionMode } = useSelector(getBodyAssessmentSelector)
  const bodySurveyState = useSelector(getBodySurveySelector)
  const dataPointsConfig: IDataPointsConfig = useSelector(getDatapointsConfigSelector)

  const { __selectedBodyParts, __selectedBodyPart } = bodySurveyState
  const [selectedBodyPart, setSelectedBodyPart] = useState<IOption | null>(null)
  const [openDropdown, setDropdown] = useState(false)
  const isObstetricsActive = selectedExam === BodyAssessmentConstants.OBSTETRICS
  const isDrainsAndTubesActive = selectedExam === BodyAssessmentConstants.DRAINS_TUBES
  const isBurnsTBSActive = selectedExam === BodyAssessmentConstants.BURNS_TBSA
  const burnsRO9Active = selectedExam === BodyAssessmentConstants.BURNS_RULE_OF_NINES
  const isBodySurveyActive = selectedExam === BodyAssessmentConstants.BODY_SURVEY

  const burnsStateData: IBurnsState = useSelector(getBurnsSelector)
  const { tbsaScore, burnsColorData, burnsExamData } = burnsStateData

  const segmentsArr = [
    {
      id: 3,
      name: '1st',
    },
    {
      id: 4,
      name: '2nd',
    },
    {
      id: 5,
      name: '3rd',
    },
  ]

  const ordinalSuffix = (num: unknown) => {
    if (num === 1) return '1st'
    if (num === 2) return '2nd'
    if (num === 3) return '3rd'
    return `${num}th` // for numbers beyond 3
  }

  let idCounter = 0

  const { examValues, examValuesPNNVs, findings } = bodySurveyState
  const combinedExamOptions = [...examValues, ...examValuesPNNVs]
  const allDocumentedLocationIds = getDocumentedLocations(combinedExamOptions, findings).map((location) => location.id)

  const isGeneralExtremitiesDataPresent = allDocumentedLocationIds.includes(10)

  const burnsSegmentArray = Object?.entries(burnsColorData).map(([key, value]) => ({
    key,
    id: idCounter++,
    value: value,
    name: ordinalSuffix(value),
  }))

  const initialSelectedSegment = burnsSegmentArray.reduce((acc: any, segment: any) => {
    acc[segment?.key] = {
      name: segment.name,
      id: segment.id,
    }
    return acc
  }, {})

  const [selectedSegment, setSelectedSegment] = useState<{ [key: string]: ISegmentButton }>({
    ...initialSelectedSegment,
  })

  useEffect(() => {
    setSelectedSegment({ ...initialSelectedSegment })
  }, [burnsColorData])

  const countGreaterThanZero = Object.values(burnsStateData?.burnsColorData).filter((value) => value > 0).length
  const isNeuroActive = selectedExam === BodyAssessmentConstants.NEURO_CONDITIONS
  const isCardiacActive = selectedExam === BodyAssessmentConstants.CARDIO_CONDITIONS
  const isMentalActive = selectedExam === BodyAssessmentConstants.MENTAL_CONDITIONS
  const isBodyPartsActive = bodySelectionMode.id === 1 && selectedExam === BodyAssessmentConstants.BODY_SURVEY
  const { DP_GENERAL_EXTREMITIES } = dataPointsConfig

  const showCount =
    !isObstetricsActive &&
    !isDrainsAndTubesActive &&
    !isBurnsTBSActive &&
    bodySelectionMode.id !== 2 &&
    bodySelectionMode.id !== 3 &&
    !isNeuroActive &&
    !isMentalActive &&
    !isCardiacActive

  const bodyPartOptions = [
    { id: 1, name: 'Body parts' },
    { id: 2, name: 'External / Skin' },
    { id: 3, name: 'General Extremities', hide: DP_GENERAL_EXTREMITIES === 0 && !isGeneralExtremitiesDataPresent },
  ]

  const handleBodyPartSelect = (option: IOption | null) => {
    setSelectedBodyPart(option)
    isTablet() && setDropdown(false)
    dispatch(setBodySelectionMode(option))
    dispatch(setDocumentFindings(false))
    dispatch(setExaminationInProgress(false))
    dispatch(selectedBodyPartAction(null))
    dispatch(setZoomVisible(false))
    setDropdown(false)
  }

  const getLabel = () => {
    if (isObstetricsActive) {
      return t('additionalAssessments.examTypes.Obstetrics')
    }
    if (isDrainsAndTubesActive) {
      return t('additionalAssessments.examTypes.DrainsTubes')
    }
    if (isBurnsTBSActive) {
      return t('additionalAssessments.examTypes.BurnsTbsa')
    }
    if (burnsRO9Active) {
      return t('additionalAssessments.examTypes.BurnsRuleOfNines')
    }

    if (isMentalActive) {
      return t('additionalAssessments.examTypes.MentalConditions')
    }
    if (isCardiacActive) {
      return t('additionalAssessments.examTypes.CardioConditions')
    }
    if (isNeuroActive) {
      return t('additionalAssessments.examTypes.NeuroConditions')
    }
    if (isBodyPartsActive) {
      if (bodySelectionMode.id === 1) {
        if (__selectedBodyParts.length > 1) {
          return t('additionalAssessments.bodySurvey.bodyGroup')
        }
        if (__selectedBodyPart?.hasZoom) {
          return __selectedBodyPart?.name
        }
        if (__selectedBodyParts.length !== 0) {
          return __selectedBodyParts[0]?.name
        }
        return bodySelectionMode?.name
      }
    }
    return bodySelectionMode?.name || t('bodyAssessment.SelectBodyPartsLabel') || ''
  }

  const getType = () => {
    if (selectedExam === BodyAssessmentConstants.BODY_SURVEY) {
      return 'dropdown'
    }
    if (selectedExam === BodyAssessmentConstants.BURNS_RULE_OF_NINES) {
      return 'tabletSupportModal'
    }
    return 'heading'
  }

  const getOtherContent = () => {
    let renderContent: React.ReactNode | null = null
    if (isDrainsAndTubesActive) {
      renderContent = <DrainsAndTubesComment />
    } else if (isObstetricsActive) {
      renderContent = <ObstetricsComment />
    }
    return !isTabletInLandscape && renderContent && <div className='exam-bar-comment-icon'>{renderContent}</div>
  }

  const actionButtonObj = [
    {
      label: 'Close',
      className: 'btn btn-secondary',
      onClick: (e: any) => {
        setDropdown(false)
      },
    },
  ]

  const labelMapping: any = {
    AdultFHead: { label: 'Head', locationId: 2 },
    AdultFChestLung: { label: 'Chest/Lung', locationId: 5 },
    AdultFPelvis: { label: 'Pelvis/GU', locationId: 8 },
    AdultFArmL: { label: 'Left Arm', locationId: 37 },
    AdultFArmR: { label: 'Right Arm', locationId: 38 },
    AdultFLegL: { label: 'Left Leg', locationId: 65 },
    AdultFLegR: { label: 'Right Leg', locationId: 66 },
    AdultFGenitalia: { label: 'Genitalia', locationId: 83 },
    AdultBHead: { label: 'Head', locationId: 2 },
    AdultBLegL: { label: 'Left Leg', locationId: 65 },
    AdultBLegR: { label: 'Right Leg', locationId: 66 },
    AdultBPelvis: { label: 'Pelvis/GU', locationId: 8 },
    AdultBBack: { label: 'Back/Spine', locationId: 9 },
    AdultBArmL: { label: 'Left Arm', locationId: 37 },
    AdultBArmR: { label: 'Right Arm', locationId: 38 },
  }

  const anteriorOrder = [
    'AdultFHead',
    'AdultFChestLung',
    'AdultFPelvis',
    'AdultFArmL',
    'AdultFArmR',
    'AdultFLegL',
    'AdultFLegR',
    'AdultFGenitalia',
  ]

  const posteriorOrder = [
    'AdultBHead',
    'AdultBPelvis',
    'AdultBBack',
    'AdultBArmL',
    'AdultBArmR',
    'AdultBLegL',
    'AdultBLegR',
  ]

  const anteriorSegments = burnsSegmentArray
    .filter((segment) => segment.key.includes('AdultF'))
    .map((segment) => ({
      ...segment,
      label: labelMapping[segment?.key]?.label || 'Unknown',
      locationId: labelMapping[segment?.key]?.locationId || 'Unknown',
    }))
    .sort((a, b) => anteriorOrder.indexOf(a.key) - anteriorOrder.indexOf(b.key))

  const posteriorSegments = burnsSegmentArray
    .filter((segment) => segment.key.includes('AdultB'))
    .map((segment) => ({
      ...segment,
      label: labelMapping[segment.key]?.label || 'Unknown',
      locationId: labelMapping[segment.key]?.locationId || 'Unknown',
    }))
    .sort((a, b) => posteriorOrder.indexOf(a.key) - posteriorOrder.indexOf(b.key))

  const updateBurnData = (segment: any, newValue: number, newSegmentName: string, type: string) => {
    let label
    switch (newSegmentName) {
      case '1st':
        label = '1st Degree'
        break
      case '2nd':
        label = '2nd Degree'
        break
      case '3rd':
        label = '3rd Degree'
        break
      default:
        label = ''
    }

    const updatedBurnType =
      newSegmentName === '1st' ? 1 : newSegmentName === '2nd' ? 2 : newSegmentName === '3rd' ? 3 : 0

    let burnPercentage = '0'
    if ([2, 37, 38].includes(segment?.locationId)) {
      burnPercentage = '4.5'
    } else if ([5, 8, 9, 65, 66].includes(segment?.locationId)) {
      burnPercentage = '9'
    } else if (segment?.locationId === 83) {
      burnPercentage = '1'
    } else {
      burnPercentage = '0' // Default value if none of the conditions are met
    }

    const newEntry = {
      burnPercentage: burnPercentage,
      burnType: updatedBurnType,
      label: label,
      locationId: segment?.locationId,
      locationName: segment?.label,
      type: type,
      unitLabel: '%',
    }

    const existingIndex = burnsExamData?.findIndex(
      (item) => item.locationId === segment?.locationId && item.type === type
    )

    let newBurnsExamData

    if (existingIndex >= 0) {
      newBurnsExamData = burnsExamData.map((item, index) => (index === existingIndex ? newEntry : item))
    } else {
      newBurnsExamData = [...burnsExamData, newEntry]
    }

    newBurnsExamData = newBurnsExamData.filter((item) => item.burnType !== 0)

    dispatch(setBurnExams(newBurnsExamData))
    dispatch(setBodyAreaCalculations())
    dispatch(saveBurns())
  }

  const handleToggleChange = (e: any, segment: any, type: string) => {
    e.preventDefault()
    const newValue = segment.name === '0th' ? 1 : 0
    const newSegmentName = ordinalSuffix(newValue)

    const newState = {
      ...burnsColorData,
      [segment.key]: newValue,
    }

    setSelectedSegment((prevSelectedSegment) => ({
      ...prevSelectedSegment,
      [segment.key]: {
        ...prevSelectedSegment[segment.key],
        name: newSegmentName,
      },
    }))

    updateBurnData(segment, newValue, newSegmentName, type)
    dispatch(setBurnColors(newState))
  }

  const handleSegmentChange = (updatedSegment: ISegmentButton, segment: any, type: string) => {
    const newSegmentName = updatedSegment.name
    const newValue = parseInt(newSegmentName) || 0

    setSelectedSegment((prevSelectedSegment) => ({
      ...prevSelectedSegment,
      [segment.key]: updatedSegment,
    }))

    const newState = {
      ...burnsColorData,
      [segment.key]: newValue,
    }

    updateBurnData(segment, newValue, newSegmentName, type)
    dispatch(setBurnColors(newState))
  }

  const renderAnteriorSegments = () => {
    return anteriorSegments.map((segment) => (
      <IonRow className='padding-10 font-size-15' key={segment.id}>
        <IonCol className='d-flex align-items-center justify-content-start' size='5'>
          <IonToggle
            className='mr-8 toggle-height'
            name={segment.key}
            checked={segment.name !== '0th'}
            onIonChange={(e) => handleToggleChange(e, segment, 'A')}
          />
          <IonLabel>{segment.label}</IonLabel>
        </IonCol>
        <IonCol size='7'>
          <ToggleSegmentButtons
            selected={selectedSegment[segment.key]}
            segmentsList={segmentsArr}
            dataTestId='burnsSegmentButton'
            disabled={segment?.name === '0th'}
            onChangeAction={(updatedSegment) => handleSegmentChange(updatedSegment, segment, 'A')}
          />
        </IonCol>
      </IonRow>
    ))
  }

  const renderPosteriorSegments = () => {
    return posteriorSegments.map((segment) => (
      <IonRow className='padding-10 font-size-15' key={segment.id}>
        <IonCol className='d-flex align-items-center justify-content-start' size='5'>
          <IonToggle
            className='mr-8 toggle-height'
            name={segment.key}
            checked={segment.name !== '0th'}
            onIonChange={(e) => handleToggleChange(e, segment, 'P')}
          />
          <IonLabel>{segment.label}</IonLabel>
        </IonCol>
        <IonCol size='7'>
          <ToggleSegmentButtons
            selected={selectedSegment[segment.key]}
            segmentsList={segmentsArr}
            dataTestId='burnsSegmentButton'
            disabled={segment?.name === '0th'}
            onChangeAction={(updatedSegment) => handleSegmentChange(updatedSegment, segment, 'P')}
          />
        </IonCol>
      </IonRow>
    ))
  }

  const footerContent = (
    <IonFooter className='ion-no-border'>
      <IonToolbar color='light' className='p-0'>
        <ActionButtons actionButtonObj={actionButtonObj} />
      </IonToolbar>
    </IonFooter>
  )

  const headerContent = (
    <IonHeader className='ion-no-border header-content'>
      <IonToolbar color='light' className='p-5'>
        <IonRow>
          <IonCol size='7' className='font-size-16 d-flex align-items-center ion-no-padding'>
            {t('additionalAssessments.examTypes.BurnsRuleOfNines')}{' '}
          </IonCol>
          <IonCol className='d-flex justify-content-end align-items-center' size='5'>
            <IonLabel className='font-size-15 font-weight-bold'>{t('burns.TBSA')} </IonLabel>
            <IonLabel className='custom-number-label'> {tbsaScore} %</IonLabel>
          </IonCol>
        </IonRow>
      </IonToolbar>
    </IonHeader>
  )

  const selectedOption = burnsRO9Active ? selectedBodyPart : isBodySurveyActive ? bodySelectionMode : null
  return (
    <>
      <MultiActionBarComponent
        id={t('bodyAssessment.BodySurvey')}
        dataTestId={t('bodyAssessment.BodyAssessmentExamBar')}
        label={getLabel()}
        type={getType()}
        className='exam-bar'
        showCaretIcon={getType() === 'dropdown' || getType() === 'tabletSupportModal'}
        onClick={() => setDropdown(!openDropdown)}
        onClose={() => {
          setDropdown(false)
        }}
        prefixContent={
          <BoxComponent id='exam-number' height='36px' width='80px' fill='dark-indigo'>
            {t('additionalAssessments.Exam')}
          </BoxComponent>
        }
        suffixContent={
          showCount && (
            <BoxComponent id='body-selection-counter' height='36px' width='35px' fill='steel-blue-gray'>
              {burnsRO9Active && (countGreaterThanZero || 0)}
              {isBodyPartsActive && __selectedBodyParts?.length}
            </BoxComponent>
          )
        }
        otherContent={getOtherContent()}
        typeProps={{
          id: 'ExamHeaderDropdown',
          dataTestId: 'ExamHeaderDropdown',
          isOpen: openDropdown,
          options: bodyPartOptions,
          resizableBoxConstraints: {
            width: 500,
            height: 500,
          },
          headerContent: headerContent,
          footerContent: footerContent,
          content: burnsStateData?.bodyView?.id === 1 ? renderAnteriorSegments() : renderPosteriorSegments(),
          selectedOption: selectedOption,
          actionHandler: handleBodyPartSelect,
          onClose: () => isTablet() && setDropdown(false),
        }}
      />
    </>
  )
}

export default ExamBarcomponent
